import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';

import { LngLatLike, Marker } from 'mapbox-gl';
import { Point } from 'geojson';

import { MotesToggleModule } from '@motes-sdk/components';
import { AutoSub, AutoSubOnDestroy, MapService } from '@motes-sdk/providers';
import { Animations } from '@motes-sdk/animations';
import { ClickOutsideDirectiveModule } from '@motes-sdk/directives';

import { MapEditorService } from '../../../../services';
import { MapEditorEventType, WaypointMovedEvent, WaypointClickedEvent } from '../../../../models/map-editor-event';

@AutoSubOnDestroy()
@Component({
    selector: 'waypoint-tooltip',
    standalone: true,
    imports: [CommonModule, MotesToggleModule, ClickOutsideDirectiveModule],
    templateUrl: './waypoint-tooltip.component.html',
    styleUrls: ['./waypoint-tooltip.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.fadeInOut(200, 200)]
})

export class WaypointTooltipComponent implements OnInit, OnDestroy {

    @ViewChild('tooltipMarkerContainer', { static: true }) tooltipMarkerContainer: ElementRef;

    public show: boolean = false;
    public waypointInfo: WaypointClickedEvent | WaypointMovedEvent;
    private markerDiv: HTMLElement;
    private mapboxMarker: Marker = null;

    constructor(
        private mapEditorService: MapEditorService,
        private mapService: MapService) { }

    ngOnInit(): void {

        AutoSub(this).reg['mapEditorEventSub'] = this.mapEditorService.watchMapEditorEvents().subscribe(async (ev) => {
            switch(ev.type) {
                case MapEditorEventType.WAYPOINT_CLICKED:
                    if(!ev.id) return this.removeTooltip();
                case MapEditorEventType.WAYPOINT_MOVED:
                    this.addTooltip(ev);
                    break;
                case MapEditorEventType.WAYPOINT_DELETED:
                    this.removeTooltip();
                    break;
                default: break;
            }
        });
    }

    private async removeTooltip() {
        this.show = false;
        if(this.mapboxMarker) this.mapboxMarker.remove();
    }

    private async addTooltip(info: WaypointClickedEvent | WaypointMovedEvent) {
        // Set waypoint info used in marker
        this.waypointInfo = info;
        // Add marker
        const map = await this.mapService.awaitInit();
        this.markerDiv = this.tooltipMarkerContainer.nativeElement;
        let point: Point = { type: 'Point', coordinates: info.position };
        this.mapboxMarker = new Marker(this.markerDiv, { offset: [-175, -23] });
        this.mapboxMarker.setLngLat(<LngLatLike>point.coordinates).addTo(map);
        this.show = true;
    }

    public async toggleSnap() {
        // Update view instantly
        this.waypointInfo.isSnapped = !this.waypointInfo.isSnapped;
        // Save waypoint in story and update route if snapped
        await this.mapEditorService.toggleWaypointSnap(this.waypointInfo.id, this.waypointInfo.storyKey);
        // Remove tooltip after fade-out animation
        this.removeTooltip();
    }

    public deleteWaypoint() {
        this.mapEditorService.deleteWaypoint(this.waypointInfo.id, this.waypointInfo.storyKey);
    }

    /**
     * Remove tooltip on outside div click.
     * This also prevents creating new tooltip while one is shown.
     */
    public outsideClick() {
        this.removeTooltip();
        // Unselect all waypoint features
        this.mapEditorService.changeToSelectMode();
    }

    ngOnDestroy(): void {
        this.removeTooltip();
    }

}
